import { useCallback, useEffect, useState } from "react";
import { ref, get } from "firebase/database";
import { db } from "../../firebaseConfig";

// Quantos dias de /logs (no máximo) percorrer procurando atividade recente,
// e quantos eventos coletar antes de parar. /logs é particionado por dia
// (logs/{yyyy-mm-dd}/{timestamp}/{codigo}), sem índice por data — por isso
// primeiro listamos as chaves de dia (shallow, barato) e só then lemos o
// conteúdo dos dias mais recentes, parando assim que já tiver eventos
// suficientes, em vez de baixar o nó inteiro.
const ACTIVITY_MAX_DAYS = 15;
const ACTIVITY_TARGET_EVENTS = 10;

/**
 * Camada central de leitura do Dashboard administrativo.
 *
 * Busca cada fonte (imoveis, imoveisPublicos, corretores, logs) UMA vez via
 * get() (sem listeners onValue, sem múltiplas leituras espalhadas pelos
 * componentes) e devolve os dados já normalizados em arrays/objetos simples.
 * Todo o cálculo de métricas fica em dashboardMetrics.js (funções puras),
 * este hook só busca e formata a matéria-prima.
 */
export default function useDashboardData() {
  const [loading, setLoading] = useState(true);
  const [imoveis, setImoveis] = useState([]);
  const [imoveisPublicos, setImoveisPublicos] = useState({});
  const [corretores, setCorretores] = useState({});
  const [activityLogs, setActivityLogs] = useState([]);
  const [error, setError] = useState(null);
  const [lastLoadedAt, setLastLoadedAt] = useState(null);

  const loadActivityLogs = useCallback(async () => {
    try {
      const daysSnap = await get(ref(db, "logs"));
      if (!daysSnap.exists()) return [];

      const dayKeys = Object.keys(daysSnap.val() || {})
        .sort()
        .reverse()
        .slice(0, ACTIVITY_MAX_DAYS);

      const collected = [];
      for (const day of dayKeys) {
        if (collected.length >= ACTIVITY_TARGET_EVENTS) break;
        // eslint-disable-next-line no-await-in-loop
        const daySnap = await get(ref(db, `logs/${day}`));
        if (!daySnap.exists()) continue;
        const dayVal = daySnap.val();
        Object.entries(dayVal).forEach(([timestamp, byCodigo]) => {
          Object.entries(byCodigo || {}).forEach(([codigo, entry]) => {
            collected.push({
              timestamp: Number(entry?.timestamp || timestamp),
              codigo: entry?.codigo || codigo,
              imovelId: entry?.imovelId || null,
              // só os NOMES dos campos alterados — o log guarda before/after
              // completos (ex.: array inteiro de imagesLinks), que não
              // precisamos carregar para um feed de atividade.
              fields: Object.keys(entry?.changes || {}),
            });
          });
        });
      }

      return collected.sort((a, b) => b.timestamp - a.timestamp).slice(0, ACTIVITY_TARGET_EVENTS);
    } catch (e) {
      console.error("Erro ao carregar atividade recente (/logs):", e);
      return [];
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [imoveisSnap, publicosSnap, corretoresSnap, activity] = await Promise.all([
        get(ref(db, "imoveis")),
        get(ref(db, "imoveisPublicos")),
        get(ref(db, "corretores")),
        loadActivityLogs(),
      ]);

      const imoveisVal = imoveisSnap.exists() ? imoveisSnap.val() : {};
      const arr = Object.entries(imoveisVal).map(([id, raw]) => ({ id, ...raw }));

      setImoveis(arr);
      setImoveisPublicos(publicosSnap.exists() ? publicosSnap.val() : {});
      setCorretores(corretoresSnap.exists() ? corretoresSnap.val() : {});
      setActivityLogs(activity);
      setLastLoadedAt(new Date());
    } catch (e) {
      console.error("Erro ao carregar dados do dashboard:", e);
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [loadActivityLogs]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    loading,
    error,
    imoveis,
    imoveisPublicos,
    corretores,
    activityLogs,
    lastLoadedAt,
    reload: load,
  };
}
